// Agent activity (#5206, epic #5200 D3/D11/G5) — what each roster agent is doing now,
// when it last ran, how its last 7 days went, and whether its mode is switched on.
// Pure: the status buckets, the per-origin -> per-agent fold, and the mode flag.
// The rows are read in `@developerz/db` (`queries/agent-activity.ts`) and handed in;
// `GET /v1/agents/activity` (`apps/api/src/routes/agents.ts`) is the one door.
//
// NO NEW TELEMETRY. Every number here is an aggregate over rows that already exist:
// `tasks` grouped on `envelope ->> 'origin'`, the gardener's `consolidated_at`
// stamps, `dispatch.model_escalated` audit rows, and for the triage judge the `raw_tasks`
// it decides (#6499). A field this module cannot derive from those is `null` — "this
// agent has no such record" — never a zero.

import {
  type AgentModeKey,
  type AgentModeWritableKey,
  agentModeWritable,
} from '../entities/agent-mode';
import { TaskStatus } from '../entities/task';
import { AGENT_IDS, AGENT_ROSTER, AgentId, type AgentRoster } from './agent-roster';
import type { RoleMachineCount } from './machine-roles';
import type { TaskOrigin } from './task-envelope';

/** How far back `window_7d` counts, from the read's own `now`. */
export const AGENT_ACTIVITY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** Which way one task status counts on the Agents page. */
export const AgentActivityBucket = {
  Running: 'running',
  Worked: 'worked',
  Failed: 'failed',
} as const;
export type AgentActivityBucket = (typeof AgentActivityBucket)[keyof typeof AgentActivityBucket];

/**
 * The bucket a task status counts in, or `null` for a status that counts NEITHER
 * way. A total `switch` with no default over {@link TaskStatus}: a status added to
 * the machine is a compile error here until someone decides how it counts.
 *
 * - `pending` has not run yet.
 * - `cancelled` counts neither way (epic #5200 G5): an operator or policy stopped it,
 *   which is neither work the agent did nor a failure it had.
 */
export function agentActivityBucket(status: TaskStatus): AgentActivityBucket | null {
  switch (status) {
    case TaskStatus.Pending:
    case TaskStatus.Cancelled:
      return null;
    case TaskStatus.Assigned:
    case TaskStatus.InProgress:
    // #6747: a paused task still holds its machine and comes back on Resume.
    case TaskStatus.Paused:
      return AgentActivityBucket.Running;
    case TaskStatus.Completed:
    case TaskStatus.AwaitingCi:
    case TaskStatus.AwaitingReview:
    case TaskStatus.Mergeable:
    case TaskStatus.Merged:
      return AgentActivityBucket.Worked;
    case TaskStatus.Failed:
      return AgentActivityBucket.Failed;
  }
}

/** The statuses of one bucket, DERIVED from {@link agentActivityBucket} so the SQL cannot hold a second copy. */
function statusesIn(bucket: AgentActivityBucket): readonly TaskStatus[] {
  return Object.values(TaskStatus).filter((s) => agentActivityBucket(s) === bucket);
}

/** Each bucket's member statuses — the lists the grouped query filters on. */
export const AGENT_ACTIVITY_STATUSES: Readonly<Record<AgentActivityBucket, readonly TaskStatus[]>> =
  {
    running: statusesIn(AgentActivityBucket.Running),
    worked: statusesIn(AgentActivityBucket.Worked),
    failed: statusesIn(AgentActivityBucket.Failed),
  };

/** One task origin's activity for one account, as the grouped query answers it. */
export type AgentOriginActivity = {
  readonly origin: TaskOrigin;
  /** Tasks in a running status now. */
  readonly running: number;
  /** Newest `updated_at` among tasks in a running, worked or failed status; null when none. */
  readonly lastRunAt: Date | null;
  /** Worked-bucket tasks with `updated_at` inside the window. */
  readonly worked: number;
  /** Failed tasks with `updated_at` inside the window. */
  readonly failed: number;
};

/** The escalation lane's read: `decision = 'escalated'` rows only. */
export type AgentEscalationActivity = {
  /** Newest escalated row's `ts`; null when the account has none. */
  readonly lastAt: Date | null;
  /** Escalated rows with `ts` inside the window. */
  readonly worked: number;
};

/**
 * The triage judge's read (#6499), over the `raw_tasks` it decides — the judge runs no
 * task, so the task fold has nothing for it.
 */
export type AgentTriageActivity = {
  /** Asks under a live claim now: `triaged`, no settle yet, lease not yet expired. */
  readonly running: number;
  /** Newest `settled_at`; null when the account's judge never decided an ask. */
  readonly lastAt: Date | null;
  /** Asks settled inside the window, whatever the verdict. */
  readonly decided: number;
  /** `pending` asks held on the judge's SELECTION (a key or model the Triage card picks). */
  readonly held: number;
};

/** One agent's activity. `null` is "no such record", never zero — see the module header. */
export type AgentActivityEntry = {
  readonly id: AgentId;
  readonly running: number | null;
  readonly lastRunAt: Date | null;
  readonly window7d: { readonly worked: number; readonly failed: number | null } | null;
  /**
   * The org-scope `enabled` of the mode switch this agent owns (`AGENT_ROSTER.mode`);
   * absent on an agent that owns no org-writable switch.
   * Only the flag — the mode's tool lists stay behind `write:prompts` (D11).
   */
  readonly modeEnabled?: boolean;
  /**
   * Asks held on this agent's model SELECTION now — present on the triage judge only
   * (#6499), the one agent whose work waits as held asks rather than parked tasks.
   */
  readonly held?: number;
  /**
   * How many of the account's own org machines run this agent's role now (#6757): `on`
   * the online, undrained machines with the role effective, over `total` own org machines
   * (`roleMachineCounts`, `./machine-roles.ts`). `null` for an agent whose switch no
   * machine door can set yet (and for Default, which owns none), and for EVERY agent when
   * the presence read did not answer — never `{ on: 0 }`, which reads as "not running".
   */
  readonly machines: RoleMachineCount | null;
};

/** The whole activity read, one entry per {@link AGENT_IDS} member, in that order. */
export type AgentActivity = { readonly agents: readonly AgentActivityEntry[] };

export type FoldAgentActivityInput = {
  readonly origins: readonly AgentOriginActivity[];
  /** Newest `repo_memory.consolidated_at` over the account's enabled repos. */
  readonly gardenerLastRunAt: Date | null;
  readonly escalation: AgentEscalationActivity;
  readonly triage: AgentTriageActivity;
  /**
   * Each org-writable mode's org-scope `enabled`, resolved by `resolveAgentMode`. Keyed on
   * the keys the org scope can write (`AGENT_MODE_WRITABLE.org`), so the day the matrix
   * opens a key at org this record is a type error until its flag is read.
   */
  readonly modeEnabled: Readonly<Record<AgentModeWritableKey<'org'>, boolean>>;
  /**
   * Per machine-writable key, the machines it is on (`roleMachineCounts`), or `null` when
   * the presence read did not answer. A key absent from the map reads `null` on its agent.
   */
  readonly machines: ReadonlyMap<AgentModeKey, RoleMachineCount> | null;
  /** Injectable for a test; production reads {@link AGENT_ROSTER}. */
  readonly roster?: AgentRoster;
};

type TaskTally = { running: number; lastRunAt: Date | null; worked: number; failed: number };

const emptyTally = (): TaskTally => ({ running: 0, lastRunAt: null, worked: 0, failed: 0 });

/** The later of two instants, where null is "none". */
function later(a: Date | null, b: Date | null): Date | null {
  if (a === null) return b;
  if (b === null) return a;
  return a.getTime() >= b.getTime() ? a : b;
}

/**
 * Fold the per-origin rows into one entry per roster agent. Pure.
 *
 * - A TASK-ORIGIN agent (any agent some origin maps to, bar the two below) sums its
 *   origins' counts and takes the newest `lastRunAt`; with no rows it reads `running: 0`,
 *   `window7d: {worked: 0, failed: 0}`, `lastRunAt: null`.
 * - An origin bound to `{ none: … }` (`pr_babysit_watch`, `setup`) counts for no agent.
 * - Gardener: `lastRunAt` from its consolidation stamp; `running` and `window7d` null.
 *   It owns `kb_fold` (#6759), and that origin's rows count for nothing here: a fold call
 *   is one step of the pass the stamp already dates.
 * - Escalation: a routing decision, not a run — `window7d.worked` counts escalations,
 *   `failed` is null (an escalation has no failed outcome), `running` null.
 * - Triage (#6499): `running` the asks under claim, `lastRunAt` the newest decision,
 *   `window7d.worked` the asks decided this week and `failed` null (a verdict is a
 *   decision, never a failed run), plus `held`: the asks waiting on its selection. It
 *   owns `triage_judge` (#6759), whose rows count for nothing here: the judge's call is
 *   the decision the asks already count.
 * - Every other agent (Default) reads all-null.
 */
export function foldAgentActivity(input: FoldAgentActivityInput): AgentActivity {
  const roster = input.roster ?? AGENT_ROSTER;
  // Seeded from the roster, not from the rows: an agent some origin maps to reads
  // zeros when it has no tasks, never the all-null of an agent with no task record.
  const tally = new Map<AgentId, TaskTally>();
  for (const binding of Object.values(roster.origin)) {
    if (typeof binding === 'string') tally.set(binding, emptyTally());
  }
  for (const row of input.origins) {
    const binding = roster.origin[row.origin];
    if (typeof binding !== 'string') continue;
    const t = tally.get(binding);
    if (t === undefined) continue;
    t.running += row.running;
    t.worked += row.worked;
    t.failed += row.failed;
    t.lastRunAt = later(t.lastRunAt, row.lastRunAt);
  }

  const modeOf = new Map<AgentId, boolean>();
  for (const [key, owner] of Object.entries(roster.mode) as [AgentModeKey, AgentId][]) {
    // Only a key the org scope can write has an org flag (#7008): a stored key no org door
    // may set has no flag to read, so its agent carries none.
    if (!agentModeWritable('org', key)) continue;
    modeOf.set(owner, input.modeEnabled[key]);
  }

  // One switch, one agent (`AgentRoster.mode`), so an agent has at most one machine count.
  const machinesOf = new Map<AgentId, RoleMachineCount>();
  for (const [key, owner] of Object.entries(roster.mode) as [AgentModeKey, AgentId][]) {
    const count = input.machines?.get(key);
    if (count !== undefined) machinesOf.set(owner, count);
  }

  const agents = AGENT_IDS.map((id): AgentActivityEntry => {
    const flag = modeOf.get(id);
    const mode = {
      ...(flag === undefined ? {} : { modeEnabled: flag }),
      machines: machinesOf.get(id) ?? null,
    };
    // Gardener and Triage read their OWN source before any task tally: each owns a
    // model-call origin (`kb_fold`, `triage_judge`, #6759), and such a task is one call
    // inside the pass or the decision already counted here, never a second run.
    if (id === AgentId.Gardener) {
      return { id, running: null, lastRunAt: input.gardenerLastRunAt, window7d: null, ...mode };
    }
    if (id === AgentId.Triage) {
      return {
        id,
        running: input.triage.running,
        lastRunAt: input.triage.lastAt,
        window7d: { worked: input.triage.decided, failed: null },
        held: input.triage.held,
        ...mode,
      };
    }
    const t = tally.get(id);
    if (t !== undefined) {
      return {
        id,
        running: t.running,
        lastRunAt: t.lastRunAt,
        window7d: { worked: t.worked, failed: t.failed },
        ...mode,
      };
    }
    if (id === AgentId.Escalation) {
      return {
        id,
        running: null,
        lastRunAt: input.escalation.lastAt,
        window7d: { worked: input.escalation.worked, failed: null },
        ...mode,
      };
    }
    return { id, running: null, lastRunAt: null, window7d: null, ...mode };
  });
  return { agents };
}
